import { MessageHistory } from '@vexalyn/context';
import { buildProjectContext, getCachedProjectContext, setCachedProjectContext } from '@vexalyn/context';
import type { Logger } from '@vexalyn/logger';
import type { AIProvider, AgentOptions, Message, ProviderTool, ToolCapableProvider, ToolExecutionContext } from '@vexalyn/shared';
import type { ToolRegistry } from '@vexalyn/tools';
import { LoopSafetyGuard } from './safety.js';
import { ToolHistory, previewToolOutput } from './tool-history.js';

export interface AgentEvent {
  type: 'text-delta' | 'tool-call' | 'tool-result';
  text?: string;
  name?: string;
  input?: unknown;
  result?: unknown;
}

export interface ToolAgentOptions extends AgentOptions {
  tools?: ToolRegistry;
  workspaceRoot?: string;
  approve?: ToolExecutionContext['approve'];
  ask?: ToolExecutionContext['ask'];
  shouldUseTools?: (message: string) => boolean;
  toolNamesForMessage?: (message: string) => readonly string[];
  planMode?: (message: string) => 'on' | 'off';
}

export interface Agent {
  readonly history: MessageHistory;
  stream(message: string): AsyncIterable<string>;
  streamEvents(message: string): AsyncIterable<AgentEvent>;
}

function normalizeToolInput(input: unknown): unknown {
  if (!input || typeof input !== 'object') return input;
  const obj = input as Record<string, unknown>;
  if ('arguments' in obj && obj.arguments && typeof obj.arguments === 'object') return obj.arguments;
  if ('input' in obj && obj.input && typeof obj.input === 'object') return obj.input;
  return input;
}

function isToolCapable(provider: AIProvider): provider is AIProvider & ToolCapableProvider {
  const candidate = (provider as unknown as { streamWithTools?: unknown }).streamWithTools;
  return typeof candidate === 'function';
}

function requiresPlanWorkflow(message: string): boolean {
  const text = message.toLowerCase();
  const wantsProject =
    /\b(website|web\s?site|app|aplikasi|project|proyek|portfolio|portofolio|landing page|game)\b/.test(text) ||
    /\b(folder|direktori|directory)\b/.test(text);
  const wantsMultipleFiles =
    /\b(html|css|javascript|typescript|script\.js|style\.css|index\.html)\b/.test(text) ||
    /,\s*[^,]+,\s*(dan|and)\s*/.test(text);
  const directBuildIntent = /\b(buat|bikin|bikinin|buatin|create|generate|implement|redesign|bangun)\b/.test(text);
  return directBuildIntent && wantsProject && wantsMultipleFiles;
}

function hasMutationIntent(message: string): boolean {
  const text = message.toLowerCase();
  return /\b(tulis|write|buat|bikin|bikinin|buatin|create|hapus|delete|edit|ubah|rename|update|ganti|tambah|add|fix|perbaiki|refactor|optimasi|optimize|implement|implementasi|generate|redesign|install|run|jalankan|execute|build|lint|test)\b/.test(text);
}

export function isToolRequest(message: string): boolean {
  const text = message.toLowerCase();
  // Use substring match (no \b at end) for words that can take Indonesian suffixes like -nya, -mu, -kan
  const hasFileReference =
    /\b(file|berkas|project|kode|code|auth|authentication|package\.json|\.env|game|aplikasi|app|website|proyek|skrip|script|js|javascript|css|html|fungsi|function|modul|module|komponen|component|kelas|class|fitur|feature)\b/.test(text) ||
    /folder|direktori|directory/.test(text) ||
    /\w+\.\w+/.test(text) ||
    /[\/\\]/.test(text);
  const commandPattern = /\b(test|build|lint|run|command|perintah|eksekusi|execute|install|yarn|pnpm|npm|git|jalankan|kompilasi|compile|deploy|start|restart)\b/.test(text);
  // webPattern: only match when there's a clear internet intent — not just the word "web" alone
  const webPattern = /\b(google|duckduckgo|fetch|download|http|https|internet|online|cek website|browsing)\b/.test(text) || /\burl\b/.test(text) || (/\bweb\b/.test(text) && /\b(search|cari|browse|scrape|crawl)\b/.test(text));
  const searchRequest = /\b(cari|search|find|temukan|lihat|tunjuk|tampil|show|dimana|where|mana)\b/.test(text) && (hasFileReference || webPattern);
  const listRequest = /\b(list|daftar|isi|tampilkan|show|lihat)\b/.test(text) && hasFileReference;
  const fileAction = /\b(baca|read|tulis|write|buat|create|hapus|delete|edit|ubah|rename|copy|salin|sama|update|tambah|add|fix|perbaiki|refactor|optimasi|optimize|implement|implementasi|generate|buat|bikin|bikinin|buatin)\b/.test(text) && hasFileReference;
  // Always use tools if there's any clear action intent regardless of file reference
  const clearAction = /\b(bikin|bikinin|buatin|buat|tambahin|perbaiki|rubah|ganti|hapus|cek|check|analisis|analyze|lanjut|lengkapi)\b/.test(text);
  return searchRequest || listRequest || fileAction || commandPattern || webPattern || clearAction;
}

export function toolNamesForMessage(message: string): readonly string[] {
  const text = message.toLowerCase();
  const hasFileReference =
    /\b(file|berkas|project|kode|code|auth|authentication|package\.json|\.env|game|aplikasi|app|website|proyek|skrip|script|js|javascript|css|html|fungsi|function|modul|module|komponen|component|kelas|class|fitur|feature)\b/.test(text) ||
    /folder|direktori|directory/.test(text) ||
    /\w+\.\w+/.test(text) ||
    /[\/\\]/.test(text);
  const commandPattern = /\b(test|build|lint|run|command|perintah|eksekusi|execute|install|yarn|pnpm|npm|git|jalankan|kompilasi|compile|deploy|start|restart)\b/.test(text);
  // webPattern: only match when there's a clear internet intent — not just the word "web" alone
  const webPattern = /\b(google|duckduckgo|fetch|download|http|https|internet|online)\b/.test(text) || /\burl\b/.test(text) || (/\bweb\b/.test(text) && /\b(search|cari|browse|scrape|crawl)\b/.test(text));
  if (!hasFileReference && !commandPattern && !webPattern) return [];

  const tools: string[] = [];
  const hasGlobPattern = /\*\*|\*\.|[\w-]+\.\w+.*\*|\{[^}]+,[^}]+\}/.test(message) || /(^|\s)(glob|pattern)\b/i.test(text);
  if (hasGlobPattern) {
    tools.push('glob');
    if (!tools.includes('read_file')) tools.push('read_file');
  }
  if (/\b(grep|regex|regexp|ekspresi reguler)\b/.test(text)) {
    tools.push('grep');
    if (!tools.includes('read_file')) tools.push('read_file');
  }
  if (/\b(cari|search|find)\b/.test(text)) {
    if (webPattern) {
      tools.push('web_search');
    } else {
      // If message contains an absolute path (C:\, D:\, /home/, etc.) → list_directory first
      const hasAbsPath = /[a-zA-Z]:[\/\\]/.test(text) || /^\/[a-z]/.test(text);
      if (hasAbsPath) tools.push('list_directory');
      tools.push('search_files');
      tools.push('semantic_search');
      // always pair: after finding files, agent needs to read them
      tools.push('read_file');
    }
  }
  if (/\b(list|daftar)\b/.test(text)) {
    tools.push('list_directory');
    // always pair: after listing, agent may read individual files
    if (!tools.includes('read_file')) tools.push('read_file');
  }
  if (/\b(baca|read|lihat|copy|salin|sama|mana)\b/.test(text) || text.includes('.env')) {
    if (!tools.includes('read_file')) tools.push('read_file');
  }
  if (/\b(hapus|delete)\b/.test(text)) {
    // "hapus" + value/key/api/isi → edit (clear the value), not delete the file
    if (/\b(key|nilai|isi|value|bagian|api|konten|variabel)\b/.test(text)) {
      tools.push('edit_file');
      if (!tools.includes('read_file')) tools.push('read_file');
    } else {
      tools.push(/folder|direktori|directory/.test(text) ? 'delete_directory' : 'delete_file');
    }
  }
  if (/\b(diagnostic|diagnostik|type ?error|error tipe|hover|tipe data|references|referensi|lsp)\b/.test(text)) {
    tools.push('lsp');
    if (!tools.includes('read_file')) tools.push('read_file');
  }
  if (/\b(edit|ubah|rename|update|ganti)\b/.test(text)) {
    tools.push('edit_file');
    if (/\b(beberapa|banyak|semua|multi|multiple|sekaligus)\b/.test(text)) tools.push('apply_patch');
    // agent needs to read the file before editing to pick the right oldText
    if (!tools.includes('read_file')) tools.push('read_file');
  }
  if (/\b(tulis|write|buat|create|salin|copy|sama)\b/.test(text)) {
    const wantsFolder = /folder|direktori|directory/.test(text);
    const wantsStructure = /\b(struktur|structure)\b/.test(text);
    const wantsGame   = !wantsStructure && /\b(game|aplikasi|app|website|proyek|project)\b/.test(text);
    if (wantsGame) {
      // Creating a project/game needs both mkdir and write_file
      tools.push('create_directory');
      tools.push('write_file');
    } else if (wantsFolder) {
      // Pure folder creation → use the dedicated directory tool
      tools.push('create_directory');
    } else {
      tools.push('write_file');
    }
  }
  if (/\b(struktur|structure)\b/.test(text) && /\b(project|proyek|folder|direktori|directory)\b/.test(text)) {
    tools.push('get_project_structure');
  }
  if (commandPattern) tools.push('run_command');
  if (/\b(fetch|download|url|http|https)\b/.test(text)) tools.push('fetch_web_page');

  if (tools.length === 0) {
    if (webPattern) return ['web_search', 'fetch_web_page'];
    return ['read_file', 'write_file', 'edit_file', 'search_files', 'semantic_search'];
  }
  return tools;
}

export function createAgent(options: ToolAgentOptions, logger?: Logger): Agent {
  const history = new MessageHistory();
  const toolTurnResponses = new Set<string>();
  const toolHistory = new ToolHistory(15);
  const projectContextPromise = options.workspaceRoot
    ? (async () => {
        const cached = getCachedProjectContext(options.workspaceRoot!);
        if (cached) return cached.markdown;
        const context = await buildProjectContext(options.workspaceRoot!);
        setCachedProjectContext(options.workspaceRoot!, context);
        return context.markdown;
      })().catch((error) => {
        logger?.warn('Gagal membangun project context:', error);
        return '';
      })
    : Promise.resolve('');
  if (options.systemPrompt) history.add({ role: 'system', content: options.systemPrompt });

  async function summarizeHistoryIfNeeded() {
    const list = history.list();
    const firstMsg = list[0];
    const hasSystem = firstMsg !== undefined && firstMsg.role === 'system';
    const startIndex = hasSystem ? 1 : 0;
    const chatMessages = list.slice(startIndex);
    
    // Only summarize when history is quite long (>14 messages) to avoid frequent blocking requests
    if (chatMessages.length > 14) {
      // Summarize the oldest 6 messages, keep the rest
      const messagesToSummarize = chatMessages.slice(0, 6);
      const remainingMessages = chatMessages.slice(6);
      
      try {
        const summaryPrompt = [
          { role: 'system' as const, content: 'Rangkum dalam 1 paragraf singkat (maks 200 kata) dalam Bahasa Indonesia: keputusan penting, file yang dimodifikasi, dan konteks utama dari percakapan berikut. Awali dengan "[Memori Utama]".' },
          ...messagesToSummarize
        ];
        
        let summary = '';
        for await (const chunk of options.provider.stream(summaryPrompt)) {
          summary += chunk;
        }
        
        history.clear();
        if (hasSystem) {
          history.add(firstMsg);
        }
        history.add({ role: 'system', content: summary.trim() });
        for (const msg of remainingMessages) {
          history.add(msg);
        }
      } catch (err) {
        logger?.warn('Gagal merangkum history chat:', err);
      }
    }
  }

  async function* events(message: string): AsyncIterable<AgentEvent> {
    await summarizeHistoryIfNeeded();
    const projectContext = await projectContextPromise;
    if (projectContext) {
      const messages = history.list();
      const systemIndex = messages.findIndex((item) => item.role === 'system');
      if (systemIndex >= 0 && !messages[systemIndex]!.content.includes('# Project Intelligence')) {
        history.clear();
        messages.forEach((item, index) => history.add(index === systemIndex
          ? { ...item, content: `${item.content}\n\n${projectContext}` }
          : item));
      }
    }
    history.add({ role: 'user', content: message });
    let response = '';
    let usedTools = false;
    try {
      const useTools = options.shouldUseTools?.(message) ?? true;
      if (useTools && options.tools && options.workspaceRoot && isToolCapable(options.provider)) {
        const allowedToolNames = options.toolNamesForMessage?.(message);
        const currentPlanMode = options.planMode?.(message) ?? 'on';
        const needsPlanWorkflow = currentPlanMode === 'on' && (requiresPlanWorkflow(message) || hasMutationIntent(message));
        let planApproved = false;
        let tasksCreated = false;
        const mutatingTools = new Set(['write_file', 'edit_file', 'apply_patch', 'delete_file', 'create_directory', 'delete_directory', 'run_command']);
        // Never hard-filter tools — always give AI the full toolset and let it decide.
        // toolNamesForMessage is only used as a hint in the instruction text, not as a gate.
        const availableTools = options.tools.list();
        const bindings: ProviderTool[] = availableTools.map((tool) => ({
          name: tool.name,
          description: tool.description,
          inputSchema: tool.inputSchema,
          execute: async (input) => {
            if (needsPlanWorkflow && mutatingTools.has(tool.name)) {
              if (!planApproved) {
                return {
                  ok: false,
                  error: 'Planning required: call create_plan first, wait for approval, then call create_tasks before mutating files.',
                };
              }
              if (!tasksCreated) {
                return {
                  ok: false,
                  error: 'Task checklist required: call create_tasks before mutating files.',
                };
              }
            }
            let result;
            try {
              result = await options.tools!.execute(tool.name, normalizeToolInput(input), { workspaceRoot: options.workspaceRoot!, approve: options.approve, ask: options.ask });
            } catch (error) {
              // Surface tool failures as a failed result so the model can react
              // instead of crashing the whole provider stream mid-turn.
              logger?.warn(`Tool ${tool.name} threw`, { error: error instanceof Error ? error.message : String(error) });
              return { ok: false, error: error instanceof Error ? error.message : String(error) };
            }
            if (tool.name === 'create_plan' && result.ok && typeof result.value === 'object' && result.value !== null && (result.value as { approved?: unknown }).approved === true) {
              planApproved = true;
            }
            if (tool.name === 'create_tasks' && result.ok) {
              tasksCreated = true;
            }
            return result;
          },
        }));
        const filteredMessages = history.list();
        const readFileInstruction = allowedToolNames?.includes('read_file')
          ? ' Saat membaca file dengan read_file, awali tampilan dengan "Berikut isi dari file <path>:" lalu tampilkan isinya sebagai teks biasa.'
          : '';
        const hasTools = bindings.length > 0;
        const suggestedTools = allowedToolNames && allowedToolNames.length > 0
          ? `Tool relevan: ${allowedToolNames.join(', ')}`
          : `Gunakan tool yang sesuai`;
        const toolListText = hasTools
          ? suggestedTools
          : 'tidak ada tool. Jawab langsung dengan teks biasa';
        const directoryInstruction = allowedToolNames?.includes('create_directory')
          ? ' Untuk membuat folder, WAJIB langsung panggil create_directory; jangan hanya menjelaskan rencana atau menampilkan contoh struktur.'
          : '';
        const structureInstruction = allowedToolNames?.includes('get_project_structure')
          ? ' Untuk menampilkan struktur project, WAJIB panggil get_project_structure.'
          : '';
        const editInstruction = allowedToolNames?.includes('edit_file')
          ? ' Untuk permintaan mengubah/edit file, setelah membaca file WAJIB lanjutkan dengan edit_file. Jangan mengklaim selesai sebelum edit_file berhasil.'
          : '';
        const planInstruction = needsPlanWorkflow
          ? ' REQUEST INI WAJIB PLAN WORKFLOW: tool pertama HARUS create_plan. Setelah approved=true, tool berikutnya HARUS create_tasks. Baru setelah itu boleh create_directory/write_file/edit_file/run_command. Buat task ringkas berbasis milestone/file utama, bukan task kecil per section. Jangan lompat langsung ke file tools.'
          : currentPlanMode === 'off'
            ? ' PLAN MODE OFF: jangan panggil create_plan atau create_tasks kecuali user secara eksplisit meminta rencana. Langsung gunakan tool yang diperlukan untuk menyelesaikan tugas.'
            : '';
        const toolInstruction = `TOOLS AKTIF — kamu WAJIB menggunakan tool untuk menyelesaikan permintaan ini. ${toolListText}.${planInstruction} Pahami intent user dulu: chat biasa jawab natural tanpa tool, baca/cek gunakan read/list/search, perubahan file gunakan write/edit, command gunakan run_command. JANGAN bilang tidak bisa akses file atau tidak punya tool — kamu sudah punya semua tool yang dibutuhkan. Langsung gunakan tool tanpa teks dulu. Setelah tool selesai, konfirmasi singkat.${directoryInstruction}${structureInstruction}${editInstruction}${readFileInstruction} edit_file: pakai allowMultiple:true jika teks target duplikat.`;
        const systemIndex = filteredMessages.findIndex((item) => item.role === 'system');
        const toolMessages = systemIndex === -1
          ? [{ role: 'system' as const, content: toolInstruction }, ...filteredMessages]
          : filteredMessages.map((item, index) => index === systemIndex
            ? { ...item, content: `${item.content}\n${toolInstruction}` }
            : item);
        // Trim long message content before sending to provider (prevents token overflow)
        // Keep up to 8000 chars — enough context without blowing token budget
        const trimMessage = (msg: Message): Message => {
          if (msg.content.length > 8000) {
            return { ...msg, content: msg.content.substring(0, 8000) + '\n...[truncated]' };
          }
          return msg;
        };
        const trimmedMessages = toolMessages.map(trimMessage);
        const safety = new LoopSafetyGuard();
        for await (const event of options.provider.streamWithTools(trimmedMessages, bindings)) {
          if (event.type === 'tool-call') safety.recordToolCall(event.name, normalizeToolInput(event.input));
          if (event.type === 'tool-result') {
            toolHistory.add({
              tool: event.name,
              input: {},
              ok: event.result.ok,
              outputPreview: previewToolOutput(event.result.value ?? event.result.error ?? ''),
            });
          }
          if (event.type === 'tool-call' || event.type === 'tool-result') usedTools = true;
          yield event.type === 'tool-call'
            ? { ...event, input: normalizeToolInput(event.input) }
            : event;
          if (event.type === 'text-delta') response += event.text;
        }
      } else {
        const noToolInstruction = 'PENTING: Untuk permintaan ini, tidak ada tool yang tersedia. Jangan mencoba memanggil/menggunakan tool apa pun. Jawablah langsung menggunakan teks biasa.';
        const systemIndex = history.list().findIndex((item) => item.role === 'system');
        const chatMessages = systemIndex === -1
          ? [{ role: 'system' as const, content: noToolInstruction }, ...history.list()]
          : history.list().map((item, index) => index === systemIndex
            ? { ...item, content: `${item.content}\n${noToolInstruction}` }
            : item);
        for await (const chunk of options.provider.stream(chatMessages)) {
          response += chunk;
          yield { type: 'text-delta', text: chunk };
        }
      }
      if (usedTools && response.trim().length > 0) toolTurnResponses.add(response);
      history.add({ role: 'assistant', content: response });
    } catch (error) {
      logger?.error('AI provider request failed', { error: error instanceof Error ? error.message : String(error) });
      throw error;
    }
  }

  return {
    history,
    stream: async function* (message: string): AsyncIterable<string> {
      for await (const event of events(message)) if (event.type === 'text-delta') yield event.text!;
    },
    streamEvents: events,
  };
}
